import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { TrendingUp, TrendingDown, Minus, ClipboardCheck } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Zeigt die Wirkung einer erledigten CRO-Empfehlung an:
 * Baseline (bei "Erledigt"-Klick festgehalten) vs. aktuelle KPIs.
 */
export default function ImpactCard({ shopId, recKey, status }) {
  const { t, lang } = useLang();

  const { data } = useQuery({
    queryKey: ["cro-impact", shopId, recKey],
    queryFn: async () =>
      (await api.get(`/shops/${shopId}/cro/actions/${recKey}/impact`)).data,
    enabled: !!shopId && !!recKey && status === "done",
    refetchOnMount: true,
  });

  if (status !== "done" || !data || !data.available) return null;

  const fmtEur = (n) =>
    new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Math.round(n || 0));

  const fmtPct = (n, digits = 2) =>
    `${(n * 100).toFixed(digits)}%`;

  const rows = [
    {
      label: t("impact_conversion"),
      metric: data.conversion_rate,
      renderValue: (v) => fmtPct(v, 2),
      renderDelta: (m) => {
        if (m.delta === 0) return "±0";
        const pp = (m.delta * 100).toFixed(2);
        return `${m.delta > 0 ? "+" : ""}${pp} pp`;
      },
    },
    {
      label: t("impact_visitors"),
      metric: data.visitors_30d,
      renderValue: (v) => new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US").format(v),
      renderDelta: (m) =>
        `${m.delta > 0 ? "+" : ""}${new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US").format(m.delta)}`,
    },
    {
      label: t("impact_revenue"),
      metric: data.monthly_revenue_eur,
      renderValue: fmtEur,
      renderDelta: (m) => `${m.delta > 0 ? "+" : ""}${fmtEur(m.delta)}`,
    },
    {
      label: t("impact_top10"),
      metric: data.keywords_top10,
      renderValue: (v) => String(v),
      renderDelta: (m) => `${m.delta > 0 ? "+" : ""}${m.delta}`,
    },
  ];

  return (
    <div
      data-testid={`impact-card-${recKey}`}
      className="mt-3 rounded-md border border-[hsl(var(--chart-3))]/30 bg-[hsl(var(--chart-3))]/[0.04] overflow-hidden"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[hsl(var(--chart-3))]/20 bg-[hsl(var(--chart-3))]/[0.06]">
        <ClipboardCheck className="h-3.5 w-3.5 text-[hsl(var(--chart-3))]" />
        <span className="text-[10px] font-mono uppercase tracking-widest">
          {t("impact_title")}
        </span>
        <span className="text-[10px] font-mono text-muted-foreground ml-auto">
          {data.days_since_baseline === 0
            ? t("impact_today")
            : t("impact_days_ago").replace("{n}", data.days_since_baseline)}
        </span>
      </div>
      <ul className="divide-y divide-[hsl(var(--chart-3))]/10">
        {rows.map((r) => {
          if (!r.metric) return null;
          const positive = r.metric.delta > 0;
          const negative = r.metric.delta < 0;
          const flat = r.metric.delta === 0;
          const DeltaIcon = flat ? Minus : positive ? TrendingUp : TrendingDown;

          return (
            <li
              key={r.label}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                  {r.label}
                </div>
                <div className="text-sm font-mono mt-0.5">
                  <span className="text-muted-foreground">{r.renderValue(r.metric.baseline)}</span>
                  <span className="mx-1.5 text-muted-foreground">→</span>
                  <span className="font-semibold">{r.renderValue(r.metric.current)}</span>
                </div>
              </div>
              <div
                className={cn(
                  "flex items-center gap-1 text-xs font-mono shrink-0",
                  flat && "text-muted-foreground",
                  positive && "text-[hsl(var(--chart-3))]",
                  negative && "text-destructive",
                )}
              >
                <DeltaIcon className="h-3 w-3" />
                {r.renderDelta(r.metric)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
